/* =========================================================================
   Home tier gating — applies the progressive-disclosure tier to the home
   surfaces and fires the one-time "The road opens" unlock moment.

   Tier 0 (new player): hero + Start + rules + Theo greeting only. Hidden: the
   content hub (Pilgrimage tile), the pilgrimage explainer row, and the Today's
   Pilgrimage quest-log card (journey.ts also bails at Tier 0).
   Tier 1 (diving deep): the hub + explainer appear; the quest card renders as
   before; Collections & Monastery live in the avatar menu (accountMenu.ts).
   ========================================================================= */
import { currentTier, isDeepModePending, hasSeenDeepModeUnlock, markDeepModeUnlockSeen, setDeepModePending, readLifetime, migrateTheoIntroduced } from './progression';
import { track } from './telemetry';
import { triggerDeepModeUnlock, forceCompanionRefresh, renderCompanionHome } from './characters/ui';
import { renderJourneyHome } from './journey';
import { renderArcade } from './arcade';
import { renderResumeCta } from './resumeGame';
import { renderPathHome, forgetPathStreak } from './pathHome';
import { isVisible } from './dom';
import { isPilgrimageEnabled } from './featureFlags';

function toggle(id: string, show: boolean): void {
  const el = document.getElementById(id);
  if (el) el.classList.toggle('hidden', !show);
}

/** Reflect the current tier onto the home surfaces. Cheap; called on every home
    render (after renderCompanionHome, before renderJourneyHome). */
export function applyHomeTiers(): void {
  const t1 = currentTier() === 1;
  // isPilgrimageEnabled() (featureFlags.ts) is the single kill-switch for every
  // Pilgrimage home surface; journey.ts and pathHome.ts gate on the same flag.
  const pilg = isPilgrimageEnabled();
  // The hub holds one tile per deeper surface, each gated on its own; the hub
  // itself shows whenever at least one of its tiles does (Leaderboard, at Tier 1).
  // PATH v1: with the path live the hub is the SIDEBAR STRIP beside the station
  // CTA — Monastery + Collections join it (they also stay in the avatar menu),
  // and the old Pilgrimage tile retires: the station CTA and its "See the whole
  // path →" link are the map's entry points now. Flag off, the tile was already
  // dark and the hub falls back to the Leaderboard-only grid, exactly as before.
  toggle('tilePilgrimage', false);
  toggle('tileMonastery', pilg && t1);
  toggle('tileCollections', pilg && t1);
  // Leaderboard left the home body (Eric 2026-09-06 mockup): it now lives as
  // a plain account-menu item — open to everyone incl. guests; the screen
  // carries the guest "get ranked" nudge.
  toggle('tileLeaderboard', false);
  toggle('homeHub', pilg && t1); // monastery/collections tiles only
  // Hidden for now (Eric, 2026-08-02): this explainer's "Open the map →" is the
  // last entry point into the Pilgrimage map, which still shows unfinished nodes
  // (e.g. "A Word with a Friend" — see TODO.md). Re-enable with `pilg && t1`
  // once the map's own content is ready.
  toggle('pilgExplainRow', false);
  // Keep the "Today's Pilgrimage" quest-log card hidden while paused (this also
  // covers the tier-0 case and the paint before renderJourneyHome runs).
  if (!pilg || !t1) toggle('pilgrimageCard', false);
}

/** Auth-state changed (login / register / Apple / sign-out / delete): drop the
    greeting throttle and, if home is showing, re-render every identity-sensitive
    surface (Theo card, tier gating, quest card, arcade lock, resume CTA). When
    home is NOT showing, the throttle reset alone guarantees the next home render
    is fresh. */
export function refreshHomeSurfaces(): void {
  migrateTheoIntroduced(); // an existing account signing in on a fresh device has met Theo
  forceCompanionRefresh();
  try {
    if (!isVisible('home')) return;
  } catch {
    return; // DOM not mounted (tests) — nothing to repaint
  }
  renderCompanionHome();
  applyHomeTiers();
  renderJourneyHome();
  renderArcade();
  renderResumeCta();
  forgetPathStreak(); // identity changed — re-resolve the 🔥 streak
  renderPathHome(); // AFTER renderResumeCta: it owns the plain button label
}

/** One-time deep-mode unlock: on the FIRST home render after the counter crosses
    the threshold mid-play, celebrate with the Theo overlay. Fires once ever. */
export function maybeFireDeepModeUnlock(): void {
  if (!isDeepModePending()) return;
  setDeepModePending(false); // consume regardless
  if (hasSeenDeepModeUnlock()) return; // belt-and-braces: never twice
  markDeepModeUnlockSeen();
  track('DeepModeUnlocked', { lifetime: readLifetime() });
  try {
    triggerDeepModeUnlock();
  } catch {
    /* no DOM (tests) — the flags are already set so it never re-fires */
  }
}
